/**
 * Which of the two sign-in routes this browser used last.
 *
 * Separate from `lastAccount.ts` on purpose: that remembers who, so the screen can name
 * the account a silent sign-in would reuse. This remembers where, so somebody who has
 * signed in once is not asked for an address again.
 *
 * They used to have different lifetimes, with a sign-out forgetting the account and
 * deliberately keeping the provider. That is no longer true and the reversal is the point:
 * both are cleared together by `openPlatformSignOut`, because a sign-out that kept the
 * provider rendered the front door in memory and the per-provider screen after a reload.
 * The reasoning that separated them is in that function.
 *
 * Only read when a deployment offers more than one provider. With one, there is nothing
 * to remember and the stored value is ignored rather than trusted, which is also what
 * makes a stale entry from a configuration change harmless.
 */

const KEY = 'mikeai.last-provider';

/** `localStorage`, or null where it does not exist or the browser refuses it. */
function defaultStore(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    // Private modes and blocked-storage settings throw on access rather than returning
    // null, and a sign-in screen is not worth crashing over.
    return null;
  }
}

export function readProvider(store: Storage | null = defaultStore()): string | null {
  try {
    return store?.getItem(KEY) || null;
  } catch {
    return null;
  }
}

export function rememberProvider(name: string, store: Storage | null = defaultStore()): void {
  try {
    store?.setItem(KEY, name);
  } catch {
    // Full or read-only storage. The choice is simply asked for again next time.
  }
}

export function forgetProvider(store: Storage | null = defaultStore()): void {
  try {
    store?.removeItem(KEY);
  } catch {
    // As above.
  }
}
